import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const LOCAL_KEY = 'alpha_os_notes_v2';

function loadLocal() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}'); } catch { return {}; }
}
function saveLocal(data) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(data)); } catch {}
}

// 登出态本地存储的 note 之前直接存调用方传入的原始对象，没有补 id/createdAt，
// 跟云端分支（rowToNote）返回的形状不一致——依赖 note.id 做 React key 或展示日期的
// 消费方（NotesDiary、EarlyBirdWikiModal 的跟进笔记）在登出态下都会拿到 undefined，
// 前者只是 key 警告，后者还会导致日期列直接空着。抽成纯函数方便单测（这个仓库没有
// @testing-library/react，测 hook 本身成本高，测这个纯函数就够覆盖这个 bug 了）。
export function stampLocalNote(note) {
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    type: note.type || 'research',
    text: note.text,
  };
}

/**
 * Cloud-backed notes hook.
 * - Logged in  → Supabase `notes` table, keyed by project_name
 * - Logged out → localStorage fallback
 *
 * Shape: { [projectName]: [{id, text, type, createdAt}, ...] }
 */
export function useNotes(user) {
  const [notes, setNotes] = useState(loadLocal);
  const [loading, setLoading] = useState(false);

  const rowToNote = (row) => ({
    id: row.id,
    text: row.content,
    type: row.type || 'research',
    createdAt: row.created_at,
  });

  const fetchNotes = useCallback(async () => {
    if (!user || !supabase) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });
      if (!error && data) {
        const grouped = {};
        for (const row of data) {
          if (!grouped[row.project_name]) grouped[row.project_name] = [];
          grouped[row.project_name].push(rowToNote(row));
        }
        setNotes(grouped);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotes();
    } else {
      setNotes(loadLocal());
    }
  }, [user, fetchNotes]);

  const addNote = useCallback(async (projectName, note) => {
    if (user && supabase) {
      const { data, error } = await supabase
        .from('notes')
        .insert({
          user_id: user.id,
          project_name: projectName,
          type: note.type || 'research',
          content: note.text,
        })
        .select()
        .single();
      if (!error && data) {
        const newNote = rowToNote(data);
        setNotes(prev => ({
          ...prev,
          [projectName]: [...(prev[projectName] || []), newNote],
        }));
      }
    } else {
      const stamped = stampLocalNote(note);
      setNotes(prev => {
        const updated = {
          ...prev,
          [projectName]: [...(prev[projectName] || []), stamped],
        };
        saveLocal(updated);
        return updated;
      });
    }
  }, [user]);

  /** Used by data import flow */
  const importNotes = useCallback((data) => {
    setNotes(data);
    if (!user) saveLocal(data);
  }, [user]);

  return { notes, addNote, loading, refetch: fetchNotes, importNotes };
}
